// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, Columns, Stack } from '@locus-taxy/neo-ui';
import { isEnumControl, or, rankWith, uiTypeIs } from '@jsonforms/core';

import useEnumProps from '../util/useEnumProps';
import sortByOrder from '../util/sortByOrder';

type Props = {};

const OrderCallbackFiltersControl = (props: Props): React.Node => {
    const { t } = useTranslation('platform');

    const {
        data,
        handleChange: onChange,
        path,
        translationKey,
        schema
    } = useEnumProps(props);

    const { statusEnum } = schema?.items || {};
    let { subStatusEnum } = schema?.items || {};

    // backward compatible code to handle previous object only approach
    if (!Array.isArray(subStatusEnum)) {
        subStatusEnum = [subStatusEnum];
    }

    const orderStatus = statusEnum || [];

    /**
     * A map keeps track of checked state in the format
     * {
     *  "[Statsus - eg. EXECUTING]": {
     *    isChecked: true,
     *      "[Sub Status - eg. ACCEPTED]": { isChecked: true },
     *   }
     * }
     */
    const values = React.useMemo(() => {
        const filtersMap = {};

        data.forEach(({ orderStatus, allowedSubStatuses }) => {
            filtersMap[orderStatus] = {
                isChecked: true
            };

            allowedSubStatuses?.forEach(subStatus => {
                filtersMap[orderStatus][subStatus] = {
                    isChecked: true
                };
            });
        });

        return filtersMap;
    }, [data]);

    const handleChange = (isChecked: boolean, status: string) => {
        let updatedFilters;

        if (isChecked) {
            updatedFilters = data.concat({
                orderStatus: status,
                allowedSubStatuses: []
            });
        } else {
            updatedFilters = data.filter(
                ({ orderStatus }) => orderStatus !== status
            );
        }

        onChange(path, sortByOrder(updatedFilters, orderStatus, 'orderStatus'));
    };

    const handleSubStatusChange = (
        isChecked: boolean,
        status: string,
        subStatus: string
    ) => {
        // Fetch all items stored under status eg. all keys under 'EXECUTING'.
        //   "EXECUTING": {
        //     "ACCEPTED": { "isChecked": true },
        //     "isChecked": true
        //   }
        const allKeys = Object.keys(values[status] || {});
        // All keys contains subStatuses and 'isChecked' flag so removing that.
        const selectedSubStatuses = allKeys.filter(key => key !== 'isChecked');
        // Filter out the rest of the items other than the current selected 'status'.
        const updatedFilters = data.filter(
            ({ orderStatus }) => orderStatus !== status
        );

        let updatedSubStatuses;

        if (isChecked) {
            updatedSubStatuses = selectedSubStatuses.concat(subStatus);
        } else {
            updatedSubStatuses = selectedSubStatuses.filter(
                status => status !== subStatus
            );
        }

        const orderSubStatus =
            subStatusEnum.find(({ parent }) => parent === status)?.values || [];

        updatedSubStatuses = sortByOrder(updatedSubStatuses, orderSubStatus);

        // Update the data with latest substatuses.
        onChange(
            path,
            sortByOrder(
                updatedFilters.concat({
                    orderStatus: status,
                    allowedSubStatuses: updatedSubStatuses
                }),
                orderStatus,
                'orderStatus'
            )
        );
    };

    return (
        <Box backgroundColor="white" padding={4} borderRadius="4px">
            <Stack space="xSmall">
                {orderStatus.map(status => {
                    const orderSubStatus =
                        subStatusEnum.find(({ parent }) => parent === status)
                            ?.values || [];

                    return (
                        <React.Fragment key={status}>
                            <Checkbox
                                size="medium"
                                label={t(`${translationKey}.values.${status}`)}
                                onChange={value => handleChange(!value, status)}
                                checked={values[status]?.isChecked}
                            />
                            {orderSubStatus.length ? (
                                <Box ml={40}>
                                    <Columns alignY="center" space="small" wrap>
                                        {orderSubStatus.map(subStatus => (
                                            <Box key={subStatus} width={140}>
                                                <Checkbox
                                                    size="small"
                                                    label={t(
                                                        `${translationKey}.values.${subStatus}`
                                                    )}
                                                    onChange={value =>
                                                        handleSubStatusChange(
                                                            !value,
                                                            status,
                                                            subStatus
                                                        )
                                                    }
                                                    checked={
                                                        values[status]?.[
                                                            subStatus
                                                        ]?.isChecked
                                                    }
                                                />
                                            </Box>
                                        ))}
                                    </Columns>
                                </Box>
                            ) : null}
                        </React.Fragment>
                    );
                })}
            </Stack>
        </Box>
    );
};

export default OrderCallbackFiltersControl;

export const orderCallbackFiltersControlTester: any = rankWith(
    1,
    or(uiTypeIs('OrderCallbackFiltersControl'), isEnumControl)
);
